// MediaRenderer.tsx
import React from "react";
import { Media, MediaType } from "../../../types/media.types";

export interface MediaRendererProps {
  media: Media;

  /** Width/height of the container (number = px, or any CSS string, e.g. "50%", "300px") */
  width?: number | string;
  height?: number | string;

  /** How the media should fit inside the container */
  objectFit?: "cover" | "contain" | "fill" | "none" | "scale-down";

  className?: string;
  style?: React.CSSProperties;
}

const MediaRenderer: React.FC<MediaRendererProps> = ({
  media,
  width,
  height,
  objectFit = "cover",
  className,
  style,
}) => {
  const { url, mediaType, altText } = media;

  const containerStyle: React.CSSProperties = {
    width: typeof width === "number" ? `${width}px` : width,
    height: typeof height === "number" ? `${height}px` : height,
    position: "relative",
    overflow: "hidden",
    ...style,
  };

  const mediaStyle: React.CSSProperties = {
    width: "100%",
    height: "100%",
    objectFit,
    display: "block",
  };

  const getYouTubeEmbedUrl = (youtubeUrl: string): string | null => {
    try {
      const parsed = new URL(youtubeUrl);

      if (parsed.hostname.includes("youtube.com")) {
        const v = parsed.searchParams.get("v");
        if (v) return `https://www.youtube.com/embed/${v}`;
      }

      if (parsed.hostname === "youtu.be") {
        const videoId = parsed.pathname.replace("/", "");
        if (videoId) return `https://www.youtube.com/embed/${videoId}`;
      }

      return null;
    } catch {
      return null;
    }
  };

  if (mediaType === MediaType.IMAGE) {
    return (
      <div className={className} style={containerStyle}>
        <img src={url} alt={altText || ""} style={mediaStyle} />
      </div>
    );
  }

  if (mediaType === MediaType.VIDEO) {
    return (
      <div className={className} style={containerStyle}>
        <video src={url} controls style={mediaStyle} />
      </div>
    );
  }

  if (mediaType === MediaType.YOUTUBE_VIDEO) {
    const embedUrl = getYouTubeEmbedUrl(url);

    if (!embedUrl) {
      return (
        <div className={className} style={containerStyle}>
          <a href={url} target="_blank" rel="noopener noreferrer">
            Watch on YouTube
          </a>
        </div>
      );
    }

    return (
      <div className={className} style={containerStyle}>
        <iframe
          src={embedUrl}
          title={altText || "YouTube video"}
          style={{
            ...mediaStyle,
            border: 0,
          }}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }

  return null;
};

export default MediaRenderer;
